import { Listbox, Transition } from "@headlessui/react";
import { Fragment, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import algosdk from "algosdk";

import { sendAlgosToAccount, sendAssetToAccount } from "actions/transactions";
import { sendingTransaction } from "actions/loaders";
import {
	searchAccountIDsByNFD,
	getAccountInfoById,
	getAccountNameByAccountID,
	getAllAccountsByUser,
	getNFDByAccountID,
	getAccountIDByNFD,
	getAssetInfoFromAccount,
} from "actions/accounts";

import useTheme from "hooks/useTheme";

import { findInObjectArrayByMatchingProperties } from "helpers/functions";
import SelectFromContactsModal from "theme/modals/contacts/SelectFromContactsModal";
import TransactionSuccessModal from "theme/modals/sendAsset/TransactionSuccessModal";
import { TiTick } from "react-icons/ti";

import UserIconRoundIcon from "theme/icons/UserIconRound";
import TransactionFailedModal from "theme/modals/TransactionFailedModal";
import PictureIcon from "theme/icons/Picture";
import PasteIcon from "theme/icons/Paste";
import ContactsListIcon from "theme/icons/ContactsList";
import SendIcon from "theme/icons/Send";
import ArrowBackIcon from "theme/icons/ArrowBack";
import ClearIcon from "theme/icons/Clear";
import ExpandDownIcon from "theme/icons/ExpandDown";
import ExpandUpIcon from "theme/icons/ExpandUp";
import StyledTooltipWithoutArrow from "theme/tooltips/StyledTooltipWithoutArrow";

const SendAsset = ({ navigatePage }) => {
	const location = useLocation();
	const dispatch = useDispatch();
	const { appLanguage, themeColor, baseCurrency } = useTheme();

	const algoChain = useSelector((state) => state.AppPreferences.algoChain);
	const accounts = useSelector((state) => state.Accounts);
	const accountInfo = useSelector((state) => state.AccountInfo);
	const sendingTransaction = useSelector((state) => state.SendingTransaction);

	const [currentAssetTypeIsNFT, setCurrentAssetTypeIsNFT] = useState(null);
	const [assets, setAssets] = useState(null);
	const [formData, setFormData] = useState(null);

	const [senderAccount, setSenderAccount] = useState(null);
	const [sendingAsset, setSendingAsset] = useState(null);
	const [sendingAssetAmount, setSendingAssetAmount] = useState(null);

	const [minimumBalance, setMinimumBalance] = useState(null);
	const [insufficientAmount, setInsufficientAmount] = useState(false);

	const [receiverAccount, setReceiverAccount] = useState(null);
	const [receiverFriendlyName, setReceiverFriendlyName] = useState(null);
	const [receiverProfileImage, setReceiverProfileImage] = useState(null);
	const [receiverAccountIsValid, setReceiverAccountIsValid] = useState(true);
	const [receiverAccountOptedInAsset, setReceiverAccountOptedInAsset] = useState(true);

	const [accountListByNFD, setAccountListByNFD] = useState(null);
	const [showAccountListByNFD, setShowAccountListByNFD] = useState(false);

	const [showAddNotesSection, setShowAddNotesSection] = useState(false);

	const [contactChooseModalIsOpen, setSelectFromContactsModalIsOpen] = useState(false);

	const [transactionSuccessModalIsOpen, setTransactionSuccessModalIsOpen] = useState(false);
	const [transactionSuccessModalInfo, setTransactionSuccessModalInfo] = useState(null);

	const [transactionFailedModalIsOpen, setTransactionFailedModalIsOpen] = useState(false);
	const [transactionFailedModalInfo, setTransactionFailedModalInfo] = useState(null);

	useEffect(() => {
		if (!accounts) dispatch(getAllAccountsByUser());

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (accounts && !accountInfo) dispatch(getAccountInfoById(accounts[0]["account-address"], algoChain));

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [accounts]);

	useEffect(() => {
		if (location?.state) {
			const { asset } = location.state;
			if (asset?.["is-nft"]) setCurrentAssetTypeIsNFT(true);
			else setCurrentAssetTypeIsNFT(false);

			setSendingAsset(asset);
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [location]);

	useEffect(() => {
		if (accountInfo?.assets) generateAssetListWithInfoByAccountAssets(accountInfo?.assets);

		if (accountInfo?.address) {
			const account = findInObjectArrayByMatchingProperties(accounts, { "account-address": accountInfo?.address });

			setSenderAccount(account[0]);
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [accountInfo]);

	useEffect(() => {
		if (senderAccount?.["account-address"]) {
			setAssets(null);

			dispatch(getAccountInfoById(senderAccount["account-address"], baseCurrency["currency-code"], algoChain));
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [senderAccount]);

	useEffect(() => {
		if (assets && !sendingAsset) setSendingAsset(assets[0]);

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [assets]);

	useEffect(() => {
		if (sendingAsset?.["asset-id"]) {
			setSendingAssetAmount(null);

			setMinimumBalance(
				sendingAsset["asset-id"] === -1
					? (sendingAsset.amount - accountInfo["min-balance"] - 1000) / 1000000
					: sendingAsset.amount / Math.pow(10, sendingAsset.decimals)
			);
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [sendingAsset]);

	useEffect(() => {
		if (sendingAsset && sendingAssetAmount)
			setInsufficientAmount(
				sendingAsset?.["asset-id"] === -1
					? sendingAssetAmount > minimumBalance
						? true
						: false
					: sendingAssetAmount > minimumBalance
					? true
					: false
			);

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [sendingAssetAmount]);

	useEffect(() => {
		if (receiverAccount) {
			checkIfValidAddress();
			GetAccountNFDByID();
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [receiverAccount]);

	useEffect(() => {
		if (receiverFriendlyName) {
			GetAccountIDByFriendlyName();
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [receiverFriendlyName]);

	useEffect(() => {
		if (receiverAccount && sendingAsset) CheckIfAssetExistsInAccount();

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [receiverAccount, sendingAsset]);

	const checkIfValidAddress = () => {
		try {
			const isValid = algosdk.isValidAddress(receiverAccount);

			setReceiverAccountIsValid(isValid ? isValid : false);
		} catch (error) {
			console.error(error);
		}
	};

	const CheckIfAssetExistsInAccount = async () => {
		try {
			const data = await getAssetInfoFromAccount(
				{ accountID: receiverAccount, assetID: sendingAsset?.["asset-id"] },
				algoChain
			);

			if ((data?.code === 200 && data?.status === true) || sendingAsset?.["asset-id"] === -1)
				setReceiverAccountOptedInAsset(true);
			else setReceiverAccountOptedInAsset(false);
		} catch (error) {
			console.error(error);
		}
	};

	const GetAccountNFDByID = async () => {
		try {
			const accountNFD = await getNFDByAccountID(receiverAccount);

			if (accountNFD) {
				setReceiverFriendlyName(accountNFD);
				GetAccountIDsListByFriendlyName(accountNFD);
			} else {
				const accountName = await getAccountNameByAccountID(receiverAccount);
				if (accountName) setReceiverFriendlyName(accountName);
				setAccountListByNFD(null);
			}
		} catch (error) {
			console.error(error);
		}
	};

	const GetAccountIDsListByFriendlyName = async (nfd) => {
		try {
			const accountNFDs = await searchAccountIDsByNFD(nfd);

			if (accountNFDs) {
				setAccountListByNFD(accountNFDs);
			} else {
				setAccountListByNFD(null);
			}
		} catch (error) {
			console.error(error);
		}
	};

	// Get account friendly name from NF domains By Account Address
	const GetAccountIDByFriendlyName = async () => {
		await getAccountIDByNFD(receiverFriendlyName).then((data) => {
			if (data?.properties) {
				if (data?.properties?.userDefined)
					// user defined property exists then image has a htt:// url
					// set that url as receiver profile img
					setReceiverProfileImage(data?.properties?.userDefined?.avatar);
				else if (data?.properties?.verified) {
					// if user has a verified property it would be an NFT
					// convert nft address to https url to fetch image
					// replace "ipfs://" -> "https://ipfs.io/ipfs/"
					setReceiverProfileImage(data?.properties?.verified?.avatar.replace("ipfs://", "https://ipfs.io/ipfs/"));
				}
			}
		});
	};

	const handleChange = (e) => {
		setFormData({
			...formData,
			[e.target.name]: e.target.valueAsNumber || e.target.value,
		});
	};

	const generateAssetListWithInfoByAccountAssets = async (assets) => {
		let assetsArray = [];

		const algorandAsset = {
			"asset-id": -1,
			name: "Algorand",
			"unit-name": "Algo",
			amount: accountInfo?.amount,
			verification: "",
			decimals: 6,
		};

		for (let asset of assets) {
			if (asset?.["is-nft"] === currentAssetTypeIsNFT && asset?.deleted === false) assetsArray.push(asset);
		}

		assetsArray = [...assetsArray].sort((a, b) => b?.amount - a?.amount);

		if (!currentAssetTypeIsNFT) assetsArray.unshift(algorandAsset);

		setAssets(assetsArray);
	};

	const SendTransaction = async () => {
		dispatch(sendingTransaction(true));

		if (sendingAsset["asset-id"] === -1) {
			await sendAlgosToAccount(
				{
					"receiver-account": receiverAccount,
					"sender-account": senderAccount["account-address"],
					"sending-amount": sendingAssetAmount * 1000000,
					note: formData?.note,
				},
				algoChain
			).then((data) => {
				if (data?.txID) {
					const successMessage = {
						txID: data?.txID,
						asset: sendingAsset,
						sender: senderAccount["account-address"],
						amount: sendingAssetAmount,
						receiver: receiverAccount,
						note: formData?.note,
					};

					setTransactionSuccessModalInfo(successMessage);
					setTransactionSuccessModalIsOpen(true);
				} else if (data?.error) {
					alert(data?.message);
				}
			});
		} else {
			await sendAssetToAccount(
				{
					"receiver-account": receiverAccount,
					"sender-account": senderAccount["account-address"],
					"asset-id": sendingAsset["asset-id"],
					"sending-amount": sendingAssetAmount * Math.pow(10, sendingAsset?.decimals),
					note: formData?.note,
				},
				algoChain
			).then((data) => {
				if (data?.txID) {
					const successMessage = {
						txID: data?.txID,
						asset: sendingAsset,
						sender: senderAccount["account-address"],
						amount: sendingAssetAmount,
						receiver: receiverAccount,
						note: formData?.note,
					};

					setTransactionSuccessModalInfo(successMessage);
					setTransactionSuccessModalIsOpen(true);
				} else if (data?.error) {
					const error = JSON.parse(data?.error?.response?.text);

					const failedMessage = {
						title: data?.message,
						message: <p className="break-words">{error?.message}</p>,
					};

					// setSendSwapTransactionModalIsOpen(false);
					setTransactionFailedModalInfo(failedMessage);
					setTransactionFailedModalIsOpen(true);
				}
			});
		}

		dispatch(sendingTransaction(false));
	};

	const ClearReceiverAccount = () => {
		setReceiverAccount(null);
		setReceiverFriendlyName(null);
		setReceiverProfileImage(null);
		setReceiverAccountIsValid(true);
		setReceiverAccountOptedInAsset(true);
	};

	return (
		<Transition
			as={Fragment}
			appear={true}
			show={true}
			enter="transition-opacity duration-500"
			enterFrom="opacity-0"
			enterTo="opacity-100"
		>
			<div className="flex flex-col w-full relative items-center justify-start">
				<StyledTooltipWithoutArrow
					title={appLanguage?.goBack}
					children={
						<ArrowBackIcon
							className={`absolute left-20 top-0 w-4 cursor-pointer`}
							fill={themeColor?.hex}
							onClick={() => navigatePage(-1)}
						/>
					}
				/>
				<div className="flex flex-col w-[50%] h-full items-start justify-start overflow-x-auto px-3 space-y-4">
					{/* Select Account Section */}
					<div className="relative flex flex-col w-full space-y-1">
						<div className="flex space-x-3">
							<div className="w-7"></div>
							<label className={`text-sm font-medium ${themeColor?.text}`}>{appLanguage?.fromWhichAccount}</label>
						</div>

						<Listbox
							value={senderAccount}
							onChange={(e) => {
								setSenderAccount(e);
								setSendingAsset(null);
							}}
						>
							<div className="relative mt-1 w-full">
								<div className="flex space-x-2 items-center">
									{senderAccount?.["profile-image"] ? (
										<img
											className={`w-7 object-cover object-center rounded-lg`}
											src={senderAccount["profile-image"]}
											alt="temp"
										/>
									) : (
										<PictureIcon className={`w-7`} fill={themeColor?.hex} />
									)}
									<Listbox.Button
										className={`relative w-full cursor-default rounded-lg ${themeColor?.bg} bg-opacity-10 py-2 pl-3 pr-10 text-left shadow-md`}
									>
										<div className="flex space-x-2 items-center">
											<p className={`text-sm block truncate ${themeColor?.text}`}>{senderAccount?.name}</p>
										</div>
										<span
											className={`pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2 ${themeColor?.text}`}
										>
											<ExpandDownIcon className={`w-3`} aria-hidden="true" fill={themeColor.hex} />
										</span>
									</Listbox.Button>
								</div>
								<Transition
									as={Fragment}
									enter="transition-opacity duration-300"
									enterFrom="opacity-0"
									enterTo="opacity-100"
									leave="transition ease-in duration-100"
									leaveFrom="opacity-100"
									leaveTo="opacity-0"
								>
									<Listbox.Options className="z-10 absolute mt-1 max-h-80 w-full overflow-auto rounded-lg border bg-white py-1 text-base shadow-lg">
										{accounts?.map((account, index) => (
											<Listbox.Option
												key={index}
												className={({ active }) =>
													`relative cursor-default select-none py-2 pl-10 pr-4 ${
														active ? "bg-amber-100 text-amber-900" : "text-gray-900"
													}`
												}
												value={account}
											>
												<>
													<div className="flex space-x-2 items-center">
														{account["profile-image"] ? (
															<img
																className={`w-7 object-cover object-center`}
																src={account["profile-image"]}
																alt="temp"
															/>
														) : (
															<UserIconRoundIcon className={`w-7`} fill={themeColor?.hex} />
														)}
														<span
															className={`block truncate ${themeColor?.text} ${
																senderAccount?.["account-address"] === account["account-address"]
																	? "font-bold"
																	: "font-normal"
															}`}
														>
															{account.name}
														</span>
													</div>

													{senderAccount?.["account-address"] === account["account-address"] ? (
														<span className="absolute inset-y-0 left-0 flex items-center pl-3 text-amber-600">
															<TiTick className="h-5 w-5" aria-hidden="true" />
														</span>
													) : null}
												</>
											</Listbox.Option>
										))}
									</Listbox.Options>
								</Transition>
							</div>
						</Listbox>
					</div>

					{/* Select Asset Section */}
					<div className="relative flex flex-col w-full">
						<div className="flex space-x-3">
							<div className="w-7"></div>
							<label className={`text-sm font-medium ${themeColor?.text}`}>{appLanguage?.assetToSend}</label>
						</div>

						<div className="flex flex-col w-full items-center justify-center space-y-2">
							<div className="relative flex flex-col w-full">
								<Listbox value={sendingAsset} onChange={setSendingAsset}>
									<div className="relative mt-1 w-full">
										<div className="flex space-x-2 items-center">
											<img
												className={`w-7 ${themeColor?.text}`}
												alt="asset"
												src={`${process.env.REACT_APP_DAFFI_MAINNET_URL}/assets/image?assetID=${sendingAsset?.["asset-id"]}&width=30`}
											/>
											<Listbox.Button
												className={`relative w-full cursor-default rounded-lg ${themeColor?.bg} bg-opacity-10 py-2 pl-3 pr-10 text-left shadow-md`}
											>
												<p className={`text-sm block truncate ${themeColor?.text}`}>
													{sendingAsset?.["unit-name"]}
												</p>

												<span
													className={`pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2 ${themeColor?.text}`}
												>
													<ExpandDownIcon className={`w-3`} aria-hidden="true" fill={themeColor.hex} />
												</span>
											</Listbox.Button>
										</div>
										<Transition
											as={Fragment}
											leave="transition ease-in duration-100"
											leaveFrom="opacity-100"
											leaveTo="opacity-0"
										>
											<Listbox.Options className="z-10 absolute mt-1 max-h-80 w-full overflow-auto rounded-lg border bg-white py-1 text-base shadow-lg">
												{assets?.map((asset, index) => (
													<Listbox.Option
														key={index}
														className={({ active }) =>
															`relative cursor-default select-none py-2 pl-10 pr-4 ${
																active ? "bg-amber-100 text-amber-900" : "text-gray-900"
															}`
														}
														value={asset}
													>
														<>
															<div className="flex space-x-2 items-center">
																<img
																	className={`w-7 ${themeColor?.text}`}
																	alt=""
																	src={`${process.env.REACT_APP_DAFFI_MAINNET_URL}/assets/image?assetID=${asset["asset-id"]}&width=30`}
																/>
																<span
																	className={`block truncate ${themeColor?.text} ${
																		sendingAsset?.["asset-id"] === asset["asset-id"]
																			? "font-bold"
																			: "font-normal"
																	}`}
																>
																	{asset["unit-name"]}
																</span>
															</div>

															{sendingAsset?.["asset-id"] === asset["asset-id"] ? (
																<span className="absolute inset-y-0 left-0 flex items-center pl-3 text-amber-600">
																	<TiTick className="h-5 w-5" aria-hidden="true" />
																</span>
															) : null}
														</>
													</Listbox.Option>
												))}
											</Listbox.Options>
										</Transition>
									</div>
								</Listbox>
							</div>

							<div className="flex w-full items-center justify-center space-x-2">
								<div className="w-8"></div>
								<div className="flex w-[50%] items-center justify-between">
									<div className="flex w-full items-center justify-start">
										<div className="flex flex-col w-fit items-start justify-center">
											<p className={`text-xs font-medium ${themeColor?.text}`}>{appLanguage?.balance}</p>
											<p className={`text-xs font-bold ${themeColor?.text}`}>
												{sendingAsset &&
													(sendingAsset.amount / Math.pow(10, sendingAsset.decimals)).toLocaleString(
														undefined,
														{
															maximumFractionDigits: sendingAsset.decimals,
														}
													)}
											</p>
										</div>
									</div>

									<p
										className={`text-xs font-bold ${themeColor?.text} cursor-pointer`}
										onClick={() => {
											setSendingAssetAmount(minimumBalance);
										}}
									>
										{appLanguage?.max}
									</p>
								</div>

								<div className="flex w-[50%] relative">
									<input
										className={`flex w-full text-sm text-right border rounded-lg px-2 py-2 pr-10 shadow-md ${themeColor?.bg} bg-opacity-10 focus:outline-none ${themeColor?.text}`}
										placeholder="0.0"
										type="number"
										min="0"
										max={minimumBalance}
										value={sendingAssetAmount !== null ? sendingAssetAmount : ""}
										onChange={(e) => setSendingAssetAmount(e.target.valueAsNumber)}
									/>
									<ClearIcon
										className={`w-4 absolute right-2.5 top-2.5 cursor-pointer hover:shadow-lg`}
										fill={themeColor?.hex}
										onClick={() => setSendingAssetAmount(null)}
									/>
								</div>
							</div>
						</div>
					</div>

					{/* Receiver Account Type Section */}
					<div className="flex flex-col w-full">
						<div className="flex w-full items-center justify-start space-x-3">
							{receiverProfileImage ? (
								<img className={`w-7 object-cover object-center`} src={receiverProfileImage} alt="temp" />
							) : (
								<UserIconRoundIcon className={`w-7`} fill={themeColor?.hex} />
							)}
							<p className={`text-sm font-medium ${themeColor?.text}`}>{appLanguage?.sendTo}</p>
						</div>

						<div className="flex w-full space-x-3">
							<div className="w-7"></div>

							<div className="flex flex-col w-full items-start justify-start space-y-2.5">
								<div
									className="flex flex-col w-full space-y-0"
									onFocus={() => setShowAccountListByNFD(true)}
									onBlur={() => setShowAccountListByNFD(false)}
									tabIndex="1"
								>
									<div className="relative flex">
										<input
											className={`flex text-sm w-full border rounded-lg px-2 py-2 shadow-md ${themeColor?.bg} bg-opacity-10 focus:outline-none ${themeColor?.text}`}
											placeholder={appLanguage?.receiverAccountNameAlgo}
											name="friendly-name"
											value={receiverFriendlyName ? receiverFriendlyName : ""}
											type="text"
											onChange={(e) => {
												setReceiverProfileImage(null);
												setReceiverAccount(null);
												setReceiverFriendlyName(e.target.value);
												GetAccountIDsListByFriendlyName(e.target.value);
											}}
										/>

										<ClearIcon
											className={`w-4 absolute right-2.5 top-2.5 cursor-pointer hover:shadow-lg`}
											fill={themeColor?.hex}
											onClick={() => ClearReceiverAccount()}
										/>
									</div>

									{receiverFriendlyName && accountListByNFD?.length && (
										<div className="relative flex">
											<div
												className="w-full absolute top-0 bg-white border-l shadow-lg rounded-lg max-h-80 overflow-y-auto"
												style={{ display: showAccountListByNFD ? "block" : "none" }}
											>
												{accountListByNFD.map((item, index) => (
													<div
														className="p-3 cursor-pointer"
														onClick={() => {
															setReceiverAccount(item?.depositAccount);
															setShowAccountListByNFD(false);
														}}
														key={index}
													>
														<div className="flex flex-col">
															<h1 className={`text-sm font-medium ${themeColor?.text}`}>
																{item?.name}
															</h1>
															{item?.caAlgo?.length ? (
																<p className={`text-[0.6rem] italic ${themeColor?.text}`}>
																	{item.caAlgo[0].slice(0, 5) +
																		"..." +
																		item.caAlgo[0].slice(-5)}
																</p>
															) : item?.unverifiedCaAlgo?.length ? (
																<p className={`text-[0.6rem] italic ${themeColor?.text}`}>
																	{item.unverifiedCaAlgo[0].slice(0, 5) +
																		"..." +
																		item.unverifiedCaAlgo[0].slice(-5)}
																</p>
															) : (
																item?.owner && (
																	<p className={`text-[0.6rem] italic ${themeColor?.text}`}>
																		{item.owner.slice(0, 5) + "..." + item.owner.slice(-5)}
																	</p>
																)
															)}
														</div>
													</div>
												))}
											</div>
										</div>
									)}
								</div>

								<div
									className="flex w-full items-center justify-between cursor-pointer"
									onClick={() => setSelectFromContactsModalIsOpen(true)}
								>
									<p className={`text-xs font-medium ${themeColor?.text}`}>{appLanguage?.selectFromContacts}</p>
									<ContactsListIcon className={`w-4`} fill={themeColor?.hex} />
								</div>
								<div
									className="flex w-full items-center justify-between cursor-pointer"
									onClick={async () => setReceiverAccount(await navigator.clipboard.readText())}
								>
									<p className={`text-xs font-medium ${themeColor?.text}`}>{appLanguage?.pasteFromClipboard}</p>
									<PasteIcon className={`w-4`} fill={themeColor?.hex} />
								</div>
							</div>
						</div>
					</div>

					{/* Receiver Account number section */}
					<div className="flex w-full space-x-3">
						<div className="w-7"></div>
						<div className="flex flex-col w-full space-y-1">
							<div className="flex w-full relative">
								<input
									className={`flex text-sm w-full border rounded-lg px-2 py-2 pr-10 shadow-md ${themeColor?.bg} bg-opacity-10 focus:outline-none ${themeColor?.text}`}
									placeholder={appLanguage?.receiverAccountAddress}
									name="receiver-account"
									type="text"
									value={receiverAccount ? receiverAccount : ""}
									onChange={(e) => setReceiverAccount(e.target.value)}
								/>

								<ClearIcon
									className={`w-4 absolute right-2.5 top-2.5 cursor-pointer hover:shadow-lg`}
									fill={themeColor?.hex}
									onClick={() => ClearReceiverAccount()}
								/>
							</div>
							{receiverAccount && !receiverAccountIsValid ? (
								<p className="text-xs font-bold text-burned-orange">{appLanguage?.ReceiverAccountIsNotValid}</p>
							) : (
								receiverAccount &&
								!receiverAccountOptedInAsset && (
									<p className="text-xs font-bold text-burned-orange">
										{appLanguage?.ReceiverIsNotOptedInToAsset}
									</p>
								)
							)}
						</div>
					</div>

					{/* Add Note Section */}
					<div className="flex flex-col w-full">
						<div
							className="flex w-full items-center space-x-3"
							onClick={() => setShowAddNotesSection(!showAddNotesSection)}
						>
							<div className="w-7"></div>
							<label className={`text-sm font-medium ${themeColor?.text}`}>{appLanguage?.addNote}</label>

							{showAddNotesSection ? (
								<ExpandUpIcon className={`w-4 cursor-pointer`} fill={themeColor.hex} />
							) : (
								<ExpandDownIcon className={`w-4 cursor-pointer`} fill={themeColor.hex} />
							)}
						</div>

						{showAddNotesSection && (
							<div className="flex w-full space-x-3">
								<div className="w-7"></div>

								<textarea
									className={`flex text-sm w-full border rounded-lg px-3 py-3 shadow-md ${themeColor?.bg} bg-opacity-10 focus:outline-none ${themeColor?.text}`}
									placeholder={appLanguage?.notes}
									name="note"
									type="text"
									onChange={(e) => handleChange(e)}
								/>
							</div>
						)}
					</div>

					{/* Quotation Details Section */}
					<div className="flex w-full space-x-3">
						<div className="w-7"></div>

						<div className="flex-col w-full items-start justify-start">
							<div className="flex w-full space-x-1 items-center justify-between">
								<p className={`text-sm font-medium ${themeColor?.text}`}>{appLanguage?.feess}</p>

								<div className="flex space-x-1">
									<p className={`text-sm font-medium ${themeColor?.text}`}>
										{(1000 / 1000000).toLocaleString(undefined, {
											maximumFractionDigits: 4,
										})}
									</p>
									<p className={`text-sm font-medium ${themeColor?.text}`}>Algo</p>
								</div>
							</div>
							{/* <div className="flex w-full space-x-1 items-center justify-between">
								<p className={`text-sm font-bold ${themeColor?.text}`}>Balance</p>

								<div className="flex space-x-1">
									<p className={`text-sm font-bold ${themeColor?.text}`}>
										{sendingAsset?.amount &&
											(
												sendingAsset.amount /
												(sendingAsset["asset-id"] === -1 ? 1000000 : Math.pow(10, sendingAsset.decimals))
											).toLocaleString(undefined, {
												maximumFractionDigits: 3,
											})}
									</p>
									<p className={`text-sm font-bold ${themeColor?.text}`}>{sendingAsset?.["unit-name"]}</p>
								</div>
							</div> */}
							<div className="flex w-full space-x-1 items-center justify-between">
								<p className={`text-sm font-medium ${themeColor?.text}`}>{appLanguage?.balanceAfter}</p>
								<div className="flex space-x-1">
									<p className={`text-sm font-medium ${themeColor?.text}`}>
										{sendingAsset?.amount && sendingAssetAmount
											? (
													sendingAsset.amount / Math.pow(10, sendingAsset.decimals) -
													sendingAssetAmount
											  ).toLocaleString(undefined, {
													maximumFractionDigits: sendingAsset.decimals,
											  })
											: ""}
									</p>
									<p className={`text-sm font-medium ${themeColor?.text}`}>{sendingAsset?.["unit-name"]}</p>
								</div>
							</div>
						</div>
					</div>

					{/* Submit Transaction Section */}
					<div className="flex w-full space-x-3 items-center justify-center">
						<div className="w-7"></div>

						<div className="flex flex-col w-full items-center justify-center space-y-2">
							<p className={`text-sm font-bold text-burned-orange`}>{appLanguage?.makeSureAccountNoIsAccurate}</p>

							{sendingTransaction ? (
								<img className="animate-spin mx-auto h-5 w-auto" src="/media/logo.png" alt="img" />
							) : insufficientAmount ? (
								<button
									className={`w-full px-7 py-3 rounded-2xl ${themeColor?.bg} bg-opacity-40 text-white`}
									disabled
								>
									{sendingAsset["asset-id"] === -1 ? (
										<p className="text text-sm font-medium">
											{appLanguage?.availableSend}
											<br />
											{minimumBalance}
										</p>
									) : (
										appLanguage?.sendAmountTooHigh
									)}
								</button>
							) : (
								<button
									className={`flex items-center w-fit px-7 py-3 space-x-2 rounded-2xl ${themeColor?.bg} disabled:bg-opacity-40`}
									disabled={
										sendingAssetAmount !== null &&
										sendingAssetAmount >= 0 &&
										receiverAccount &&
										receiverAccountIsValid &&
										receiverAccountOptedInAsset
											? false
											: true
									}
									onClick={() => SendTransaction()}
								>
									<SendIcon className={`w-5`} fill="white" />
									<p className={`text-md font-bold text-white`}>{appLanguage?.send}</p>
								</button>
							)}
						</div>
					</div>
				</div>

				<SelectFromContactsModal
					isOpen={contactChooseModalIsOpen}
					setIsOpen={setSelectFromContactsModalIsOpen}
					setReceiverAccount={setReceiverAccount}
					setReceiverFriendlyName={setReceiverFriendlyName}
					setReceiverProfileImage={setReceiverProfileImage}
				/>

				<TransactionSuccessModal
					isOpen={transactionSuccessModalIsOpen}
					setIsOpen={setTransactionSuccessModalIsOpen}
					modalInfo={transactionSuccessModalInfo}
					navigatePage={navigatePage}
				/>

				<TransactionFailedModal
					isOpen={transactionFailedModalIsOpen}
					setIsOpen={setTransactionFailedModalIsOpen}
					modalInfo={transactionFailedModalInfo}
				/>
			</div>
		</Transition>
	);
};

export default SendAsset;
